import { Injectable, signal, Signal, inject } from '@angular/core';
import { WordData } from '../services/popup.service'; // Import WordData
import {
  AudioGenerated,
  ChatMessage,
  ChatRole,
  ContextType,
  IAgentCard,
  IConversationFlow,
  IConversationSettings,
  IConversationSettingsDTO,
  MessageContent,
} from '../models/agent.models';
import { MessageProcessingService } from './message-processing.service';
import { DCConversationPromptBuilderService } from './dc-conversation-builder.service';
import { buildObjectTTSRequest } from '../components/chat-container/chat-messages-list/chat-message/chat-message.utils';
import { USER_DATA_EXCHANGE, UserDataExchangeAbstractService } from '../models/user-data-exchange';
import { MicStatusLiteral } from '@dataclouder/ngx-mic';
import { nanoid } from 'nanoid';
import { EvaluationService } from './evaluation.service';
import { DefaultAgentCardsService } from './default-agent-service';
import { TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core';
import { MessagesStateService } from './messages-state.service';
import { DynamicFlowService } from './dynamic-flow.service';

export const EvaluationStructureString = `
{
  "text": "",
  "score": 0,
}
`;
export type EvaluationJson = {
  text: string;
  score: number;
};

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  private defaultAgentCardService = inject(DefaultAgentCardsService);

  // Services
  private toastAlerts = inject(TOAST_ALERTS_TOKEN);
  private messageProcessingService = inject(MessageProcessingService);
  private conversationBuilder = inject(DCConversationPromptBuilderService);
  private userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);
  private evaluationService = inject(EvaluationService);
  private messagesStateService = inject(MessagesStateService);
  private dynamicFlowService = inject(DynamicFlowService);

  // State Signals
  private isThinkingSignal = signal<boolean>(false);
  private conversationSettingsState = signal<IConversationSettings | null>(null);
  private isDestroyedSignal = signal<boolean>(false);
  public micStatusSignal = signal<MicStatusLiteral>('ready');
  public currentAudioStatus = signal<{ message: MessageContent; completed: boolean } | null>(null); // Orchestrator notifies about audio state
  private wordClickedSignal = signal<WordData | null>(null); // Signal for clicked word
  // Var State
  private avatarImages = { userImg: 'assets/defaults/avatar_user.png', assistantImg: 'assets/defaults/avatar_ai.webp' };

  /**
   * Notifies subscribers that a word has been clicked in a message.
   * @param wordData The data associated with the clicked word.
   */
  public notifyWordClicked(wordData: WordData): void {
    this.wordClickedSignal.set(wordData);
  }

  /**
   * Gets the signal that emits when a word is clicked.
   * @returns A signal emitting WordData or null.
   */
  public getWordClickedSignal(): Signal<WordData | null> {
    return this.wordClickedSignal;
  }

  public createNewUserMessage(): string {
    const message: ChatMessage = { content: '...', role: ChatRole.User };
    const processedMessage = this.messageProcessingService.processMessage(message, this.conversationSettingsState(), this.avatarImages);
    this.messagesStateService.addMessage(processedMessage);
    return processedMessage.messageId;
  }

  // Get thinking state as a signal
  isThinking(): Signal<boolean> {
    return this.isThinkingSignal;
  }

  // Set destroyed state
  setDestroyed(value: boolean): void {
    this.isDestroyedSignal.set(value);
  }

  private overrideSettingsByUserSettings(conversationSettings: IConversationSettings) {
    const userSettings = this.userDataExchange.getUserChatSettings();

    if (userSettings?.model?.provider) {
      conversationSettings.model = userSettings.model;
    }
  }

  setupConversationWithAgentCard(agentCard: IAgentCard, parseDict: { [key: string]: string } = null, conversationFlowApp: IConversationFlow = null) {
    // Basically extract settings and flow from agent card, and overrider with user seetings is needed.
    this.avatarImages.assistantImg = agentCard?.assets?.image?.url || this.avatarImages.assistantImg; // Set user AI avatar image

    // Aqui es la parte crucial de los settings definir prioridad.
    debugger;
    const conversationSettings = this.conversationBuilder.buildConversationSettings(agentCard, parseDict);
    // Here i need some rules to see when i will override and what.
    this.overrideSettingsByUserSettings(conversationSettings);
    this.conversationSettingsState.set(conversationSettings);
    // this.conversationFlowSignal.set(this.conversationBuilder.buildConversationFlow(agentCard, parseDict));

    if (conversationFlowApp) {
      // Just replace the default if agent card has a value

      if (agentCard.conversationFlow) {
        if (agentCard.conversationFlow?.goal?.task) {
          conversationFlowApp.goal = agentCard.conversationFlow.goal;
        }
        if (agentCard.conversationFlow?.dynamicConditions) {
          conversationFlowApp.dynamicConditions = agentCard.conversationFlow.dynamicConditions;
        }

        if (agentCard.conversationFlow?.triggerTasks) {
          // Leave default if a concreate task is not provided.
          if (agentCard.conversationFlow.triggerTasks?.onUserMessage?.task) {
            conversationFlowApp.triggerTasks.onUserMessage = agentCard.conversationFlow.triggerTasks.onUserMessage;
          }
          if (agentCard.conversationFlow.triggerTasks?.onUserMessage?.task) {
            conversationFlowApp.triggerTasks.onUserMessage = agentCard.conversationFlow.triggerTasks.onUserMessage;
          }
          agentCard.conversationFlow.triggerTasks = { ...agentCard.conversationFlow.triggerTasks, ...conversationFlowApp.triggerTasks };
        }

        if (agentCard.conversationFlow?.bonus) {
          conversationFlowApp.bonus = agentCard.conversationFlow.bonus;
        }
      }

      this.dynamicFlowService.setConversationFlow(conversationFlowApp);
    }
  }

  async initConversationWithSettings(conversationSettings: IConversationSettings, conversationFlow: IConversationFlow): Promise<void> {
    this.overrideSettingsByUserSettings(conversationSettings);
    this.conversationSettingsState.set(conversationSettings);
    this.dynamicFlowService.setConversationFlow(conversationFlow);
    await this.initConversation();
  }

  private async initConversation() {
    this.avatarImages.userImg = this.userDataExchange.getUserDataExchange().imgUrl;
    const conversationSettings = this.conversationSettingsState();

    for (const i in conversationSettings.messages) {
      if (!conversationSettings.messages[i].messageId) {
        conversationSettings.messages[i].messageId = nanoid();
      }
    }

    // Find first assistant message
    const firstAssistantMsg = conversationSettings?.messages.find((message) => message.role === ChatRole.Assistant);

    this.messagesStateService.clearMessages(); // Clear previous messages
    (conversationSettings?.messages || []).forEach((msg) => this.messagesStateService.addMessage(msg));

    if (firstAssistantMsg) {
      // Process the first assistant message
      const processedMessage = this.messageProcessingService.processMessage(firstAssistantMsg, this.conversationSettingsState(), this.avatarImages);
      this.messagesStateService.updateMessage(firstAssistantMsg.messageId, processedMessage);

      // Find the index of the message with the matching ID
      const messageIndex = conversationSettings.messages.findIndex((message) => message.messageId === firstAssistantMsg.messageId);

      // If found, replace the message at that index
      if (messageIndex !== -1) {
        conversationSettings.messages[messageIndex] = processedMessage;
      }
      this.runAssistantTrigger(firstAssistantMsg);
    } else if (conversationSettings?.autoStart) {
      // Auto-start conversation if configured
      await this.sendCurrentConversation();
    }
  }

  // Initialize conversation
  async initConversationWithAgentCard(
    agentCard: IAgentCard,
    parseDict: { [key: string]: string } = null,
    conversationFlow: IConversationFlow = null,
  ): Promise<void> {
    this.setupConversationWithAgentCard(agentCard, parseDict, conversationFlow);
    await this.initConversation();
  }

  /**
   * Sends a user message, processes it, adds it to the conversation,
   * and triggers the AI response flow.
   * @param message The initial ChatMessage object from the user.
   * @param updateId Optional ID if this message should update an existing one (e.g., transcription added).
   * @param updateId Optional ID if this message should update an existing one (e.g., transcription added).
   * @returns A promise resolving to an object containing the user message ID and the assistant message ID (or null if an error occurred).
   */
  async prepareUserMsnAndSend(
    message: ChatMessage,
    updateId: string | null = null,
  ): Promise<{ userMessageId: string; assistantMessageId: string | null } | null> {
    if (this.isThinkingSignal()) {
      console.warn('AI is already thinking, message dropped.');
      return null; // Indicate message was not sent due to AI being busy
    }

    let userMessageId: string;

    if (updateId) {
      // if you pass updateId means message exists and you want to update it.
      const messageToUpdate = { ...message, messageId: updateId };
      const updated = this.messagesStateService.updateMessage(updateId, messageToUpdate);
      if (!updated) {
        console.error(`Failed to update message with ID: ${updateId}`);
        // Decide how to handle this - maybe throw an error or return null?
        return null;
      }
      userMessageId = updateId;
    } else {
      // Means is new meessage, Process and add the new message
      const processedMessage = this.messageProcessingService.processMessage(message, this.conversationSettingsState(), this.avatarImages);
      // Ensure ID exists (processMessage should handle this, but fallback just in case)
      processedMessage.messageId = processedMessage.messageId || nanoid();
      this.messagesStateService.addMessage(processedMessage);
      userMessageId = processedMessage.messageId;
    }

    // Set thinking state and get AI response
    this.isThinkingSignal.set(true);
    let assistantMessageId: string | null = null;

    // Trigger are asyn Run triggers in background
    this.runUserFlowEvaluations({ messageId: userMessageId, role: ChatRole.User });

    try {
      assistantMessageId = await this.sendCurrentConversation();
    } catch (error) {
      console.error('Error sending conversation to AI:', error);
      // TODO: Handle error appropriately, maybe set a specific state or message
    } finally {
      this.isThinkingSignal.set(false);
    }

    return { userMessageId, assistantMessageId };
  }

  // Send current conversation to AI
  async sendCurrentConversation(): Promise<string | null> {
    if (this.isDestroyedSignal()) {
      return null;
    }

    const messages = this.messagesStateService.getMessagesSignal()();
    const conversationSettings = this.conversationSettingsState();

    if (messages.length > 31) {
      // Safety limit to prevent infinite conversations
      this.toastAlerts.warn({
        title: 'Conversation limit reached',
        subtitle: 'The conversation has reached its maximum length. Please start a new conversation.',
      });

      return null;
    }

    let conversationMessages = messages;

    // Add last prompt if available
    if (conversationSettings.last_prompt) {
      conversationMessages = [...messages, { content: conversationSettings.last_prompt, role: ChatRole.System }];
    }

    // Prepare conversation DTO
    const conversation: IConversationSettingsDTO = {
      messages: conversationMessages,
      conversationType: conversationSettings.conversationType,
      textEngine: conversationSettings.textEngine,
      model: conversationSettings.model || { modelName: '', provider: '' },
    };

    this.isThinkingSignal.set(true);

    const response = await this.defaultAgentCardService.callChatCompletion(conversation);

    if (!response) {
      console.error('No message returned from AI, is your service working?');
      throw new Error('No message returned from AI');
    }

    // Process response
    const newMessage: ChatMessage = this.messageProcessingService.processMessage(
      { content: response.content as string, role: ChatRole.Assistant },
      conversationSettings,
      this.avatarImages,
    );
    this.messagesStateService.addMessage(newMessage);
    this.isThinkingSignal.set(false);

    this.runAssistantTrigger(newMessage); // Not waiting should be parallel.
    return newMessage.messageId;
  }

  // Reset conversation
  async resetConversation(agentCard?: IAgentCard): Promise<void> {
    // Clear messages
    this.messagesStateService.clearMessages();
  }

  public async getTTSFile(message: MessageContent): Promise<AudioGenerated> {
    const userChatSettings = await this.userDataExchange.getUserChatSettings();

    const ttsRequest = buildObjectTTSRequest(message, userChatSettings);
    return this.defaultAgentCardService.getTextAudioFile(ttsRequest);
  }

  private async runAssistantTrigger(message: ChatMessage) {
    const userChatSettings = this.userDataExchange.getUserChatSettings();
    if (!userChatSettings.assistantMessageTask) {
      return;
    }

    const trigger = this.dynamicFlowService.conversationFlowState().triggerTasks.onAssistantMessage;
    console.log('Trigger:', trigger);
    if (trigger) {
      // const contextMessages = getContextMessages(this.messagesSignal(), ContextType.LastAssistantMessage);
      // i cant override task in trigger.
      const task = trigger.task + ' Here is the text: ' + (message.text || message.content);

      const result: EvaluationJson = await this.evaluationService.evaluateWithTask({ model: trigger.model, task });
      console.log('Assistant trigger evaluation result:', result);
      message.evaluation = result;
      this.messagesStateService.updateMessage(message.messageId, message);
    }
  }

  private async runUserFlowEvaluations(message: ChatMessage) {
    const goal = this.dynamicFlowService.conversationFlowState().goal;
    // 1) Run Goal Evaluation
    this.evaluationService.evaluateGoal(goal, this.messagesStateService.getMessagesSignal()());
    // 2) Run User Trigger Evaluation only if user allows it
    const chatUserSettings = this.userDataExchange.getUserChatSettings();
    if (chatUserSettings.userMessageTask) {
      const trigger = this.dynamicFlowService.conversationFlowState().triggerTasks.onUserMessage;

      if (trigger) {
        this.evaluationService
          .evaluateConversation(this.messagesStateService.getMessagesSignal()(), trigger, ContextType.LastUserAndPreviousAssistant)
          .then((evaluation) => {
            message.evaluation = evaluation;
            this.messagesStateService.updateMessage(message.messageId, message);
            console.log('Trigger evaluation result:', evaluation);
          });
      }
    }
  }
}
